import { createAction, props } from '@ngrx/store';
import { Hotspot, IDisplaySlide } from '@pcp/core/interfaces';
import { UserClickedOnAddToFavoritesButtonEvent } from './events/favorite';

/**
 * Public classroom event namespace (`ClassroomEvents`) — the only NgRx actions
 * the classroom umbrella exports for cross-feature coordination.
 *
 * Contract (T1 tier, docs/architecture-review-classroom.md R3):
 * - past-tense facts or user-gesture events with ≥2 actual/plausible listeners
 * - facts are dispatched only from inside the umbrella; gesture events are
 *   dispatched by the lib that owns the gesture
 * - external libs listen via `ofType(...)`; they never dispatch facts
 * - never a state-carrying feed for external reducers — notifications, not data
 *
 * Commands are never public actions: imperative asks go through the facades
 * (`ClassroomFacade` for Y.Doc state, `LessonCommandFacade` for ephemeral
 * SignalR commands).
 */

/**
 * User re-clicked the currently-shown slide: reset its dynamic content
 * (drawings, bullet points, perception overlays). Dispatched from the teacher
 * workspace; listeners include toolbar tool-deactivation, workspace-reducer
 * cleanup, driverscam stop, and the classroom-owned Y.Doc reset effect.
 */
export const userResetActiveSlide = createAction('[Classroom] User reset the active slide');

/**
 * A hotspot was activated on a displayed slide — locally by the user or
 * relayed from the beamer. Listeners: the hotspot latch (preview-screen) and
 * the beamer→teacher SignalR relay.
 */
export const hotspotActivated = createAction(
    '[Classroom] Hotspot activated',
    props<{ hotspot: Hotspot; slide: IDisplaySlide }>(),
);

/**
 * User clicked the add-to-favorites button on the preview screen. Payload
 * carries `data` and `slideTypeCategory` besides the slide because the
 * favorite-save dialog needs them.
 */
export const addedToFavorites = createAction(
    '[Classroom] Added to favorites',
    props<UserClickedOnAddToFavoritesButtonEvent>(),
);
